import React, { useState } from 'react';
import {
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Paper,
  IconButton,
  Button,
  Chip,
  Collapse,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  TerraformAttributeSchema,
  TerraformBlockSchema,
  ModuleVariable,
  ParsedVariableType,
} from '../services/terraform-api';

// ============================================================================
// Types
// ============================================================================

interface FormRendererProps {
  schema?: TerraformBlockSchema;
  moduleVariables?: ModuleVariable[];
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  mode?: 'create' | 'edit';
}

interface FieldProps {
  name: string;
  value: any;
  onChange: (value: any) => void;
  attrSchema?: TerraformAttributeSchema;
  moduleVar?: ModuleVariable;
  level?: number;
}

// ============================================================================
// Field type detection
// ============================================================================

function getBaseType(type: any): string {
  if (typeof type === 'string') return type;
  if (Array.isArray(type)) return type[0];
  return 'string';
}

function getElementType(type: any): string {
  if (Array.isArray(type) && type.length > 1) {
    if (typeof type[1] === 'string') return type[1];
    return 'string';
  }
  return 'string';
}

// ============================================================================
// Individual Field Components
// ============================================================================

const StringField: React.FC<FieldProps> = ({ name, value, onChange, attrSchema, moduleVar }) => {
  const isSensitive = attrSchema?.sensitive || moduleVar?.sensitive;
  const description = attrSchema?.description || moduleVar?.description || '';
  const isRequired = attrSchema?.required || moduleVar?.required;

  return (
    <TextField
      fullWidth
      size="small"
      label={name}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      type={isSensitive ? 'password' : 'text'}
      required={isRequired}
      helperText={description}
      placeholder={moduleVar?.default !== undefined ? `Default: ${JSON.stringify(moduleVar.default)}` : undefined}
    />
  );
};

const NumberField: React.FC<FieldProps> = ({ name, value, onChange, attrSchema, moduleVar }) => {
  const description = attrSchema?.description || moduleVar?.description || '';
  const isRequired = attrSchema?.required || moduleVar?.required;

  return (
    <TextField
      fullWidth
      size="small"
      label={name}
      value={value ?? ''}
      onChange={e => {
        const v = e.target.value;
        onChange(v === '' ? undefined : Number(v));
      }}
      type="number"
      required={isRequired}
      helperText={description}
      placeholder={moduleVar?.default !== undefined ? `Default: ${moduleVar.default}` : undefined}
    />
  );
};

const BoolField: React.FC<FieldProps> = ({ name, value, onChange, attrSchema, moduleVar }) => {
  const description = attrSchema?.description || moduleVar?.description || '';

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={value ?? false}
            onChange={e => onChange(e.target.checked)}
            size="small"
          />
        }
        label={name}
      />
      {description && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5 }}>
          {description}
        </Typography>
      )}
    </Box>
  );
};

const ListField: React.FC<FieldProps & { elementType?: string }> = ({ name, value, onChange, attrSchema, moduleVar, elementType = 'string' }) => {
  const items: any[] = Array.isArray(value) ? value : [];
  const [inputValue, setInputValue] = useState('');
  const description = attrSchema?.description || moduleVar?.description || '';

  const addItem = () => {
    if (!inputValue.trim()) return;
    const newVal = elementType === 'number' ? Number(inputValue) : inputValue;
    onChange([...items, newVal]);
    setInputValue('');
  };

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{name}</Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" display="block">{description}</Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, mt: 0.5 }}>
        {items.map((item, i) => (
          <Chip
            key={i}
            label={String(item)}
            size="small"
            onDelete={() => removeItem(i)}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
          placeholder={`Add ${elementType}...`}
          sx={{ flex: 1 }}
        />
        <IconButton size="small" onClick={addItem}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const MapField: React.FC<FieldProps> = ({ name, value, onChange, attrSchema, moduleVar }) => {
  const entries: [string, any][] = value && typeof value === 'object' ? Object.entries(value) : [];
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const description = attrSchema?.description || moduleVar?.description || '';

  const addEntry = () => {
    if (!newKey.trim()) return;
    onChange({ ...value, [newKey]: newValue });
    setNewKey('');
    setNewValue('');
  };

  const removeEntry = (key: string) => {
    const updated = { ...value };
    delete updated[key];
    onChange(updated);
  };

  const updateEntry = (oldKey: string, newVal: string) => {
    onChange({ ...value, [oldKey]: newVal });
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{name}</Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" display="block">{description}</Typography>
      )}
      {entries.map(([k, v]) => (
        <Box key={k} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
          <TextField size="small" value={k} disabled sx={{ width: '40%' }} />
          <TextField
            size="small"
            value={v ?? ''}
            onChange={e => updateEntry(k, e.target.value)}
            sx={{ flex: 1 }}
          />
          <IconButton size="small" onClick={() => removeEntry(k)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
        <TextField
          size="small"
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          placeholder="Key"
          sx={{ width: '40%' }}
        />
        <TextField
          size="small"
          value={newValue}
          onChange={e => setNewValue(e.target.value)}
          placeholder="Value"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEntry(); } }}
          sx={{ flex: 1 }}
        />
        <IconButton size="small" onClick={addEntry}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

// ============================================================================
// Nested Object / Block Fields
// ============================================================================

const NestedSingleField: React.FC<{
  name: string;
  value: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  attributes: Record<string, TerraformAttributeSchema>;
  description?: string;
  level: number;
}> = ({ name, value, onChange, attributes, description, level }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Paper variant="outlined" sx={{ p: 1, ml: level > 1 ? 1 : 0 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: expanded ? 1 : 0 }}
        onClick={() => setExpanded(!expanded)}
      >
        <ExpandMoreIcon
          sx={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: '0.2s', mr: 0.5 }}
          fontSize="small"
        />
        <Typography variant="subtitle2">{name}</Typography>
        {description && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {description}
          </Typography>
        )}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 1 }}>
          {Object.entries(attributes).map(([attrName, attrSchema]) => {
            if (attrSchema.computed && !attrSchema.optional) return null;
            return (
              <SchemaField
                key={attrName}
                name={attrName}
                value={(value || {})[attrName]}
                onChange={v => onChange({ ...value, [attrName]: v })}
                attrSchema={attrSchema}
                level={level + 1}
              />
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
};

const NestedListField: React.FC<{
  name: string;
  value: any[];
  onChange: (value: any[]) => void;
  attributes: Record<string, TerraformAttributeSchema>;
  description?: string;
  level: number;
}> = ({ name, value, onChange, attributes, description, level }) => {
  const items = Array.isArray(value) ? value : [];

  const addItem = () => {
    onChange([...items, {}]);
  };

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, updated: Record<string, any>) => {
    const newItems = [...items];
    newItems[idx] = updated;
    onChange(newItems);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">{name}</Typography>
        {description && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {description}
          </Typography>
        )}
        <Button size="small" startIcon={<AddIcon />} onClick={addItem} sx={{ ml: 'auto' }}>
          Add
        </Button>
      </Box>
      {items.map((item, idx) => (
        <Paper key={idx} variant="outlined" sx={{ p: 1, mb: 1, ml: level > 1 ? 1 : 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {name} #{idx + 1}
            </Typography>
            <IconButton size="small" onClick={() => removeItem(idx)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {Object.entries(attributes).map(([attrName, attrSchema]) => {
              if (attrSchema.computed && !attrSchema.optional) return null;
              return (
                <SchemaField
                  key={attrName}
                  name={attrName}
                  value={(item || {})[attrName]}
                  onChange={v => updateItem(idx, { ...item, [attrName]: v })}
                  attrSchema={attrSchema}
                  level={level + 1}
                />
              );
            })}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

const NestedMapField: React.FC<{
  name: string;
  value: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  attributes: Record<string, TerraformAttributeSchema>;
  description?: string;
  level: number;
}> = ({ name, value, onChange, attributes, description, level }) => {
  const entries = value && typeof value === 'object' ? Object.entries(value) : [];
  const [newKey, setNewKey] = useState('');

  const addEntry = () => {
    if (!newKey.trim()) return;
    onChange({ ...value, [newKey]: {} });
    setNewKey('');
  };

  const removeEntry = (key: string) => {
    const updated = { ...value };
    delete updated[key];
    onChange(updated);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">{name}</Typography>
        {description && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {description}
          </Typography>
        )}
      </Box>
      {entries.map(([key, entry]) => (
        <Paper key={key} variant="outlined" sx={{ p: 1, mb: 1, ml: level > 1 ? 1 : 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" fontWeight="bold">
              Key: {key}
            </Typography>
            <IconButton size="small" onClick={() => removeEntry(key)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {Object.entries(attributes).map(([attrName, attrSchema]) => {
              if (attrSchema.computed && !attrSchema.optional) return null;
              return (
                <SchemaField
                  key={attrName}
                  name={attrName}
                  value={(entry || {})[attrName]}
                  onChange={v => onChange({ ...value, [key]: { ...entry, [attrName]: v } })}
                  attrSchema={attrSchema}
                  level={level + 1}
                />
              );
            })}
          </Box>
        </Paper>
      ))}
      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
        <TextField
          size="small"
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          placeholder="New key name"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEntry(); } }}
          sx={{ flex: 1 }}
        />
        <Button size="small" onClick={addEntry} startIcon={<AddIcon />}>Add</Button>
      </Box>
    </Box>
  );
};

// ============================================================================
// Schema-driven Field Router
// ============================================================================

const SchemaField: React.FC<FieldProps> = ({ name, value, onChange, attrSchema, moduleVar, level = 0 }) => {
  if (!attrSchema && !moduleVar) {
    return <StringField name={name} value={value} onChange={onChange} />;
  }

  // Handle nested_type from provider schema
  if (attrSchema?.nested_type) {
    const { attributes, nesting_mode } = attrSchema.nested_type;
    if (nesting_mode === 'single') {
      return (
        <NestedSingleField
          name={name}
          value={value || {}}
          onChange={onChange}
          attributes={attributes}
          description={attrSchema.description}
          level={level}
        />
      );
    }
    if (nesting_mode === 'list' || nesting_mode === 'set') {
      return (
        <NestedListField
          name={name}
          value={value || []}
          onChange={onChange}
          attributes={attributes}
          description={attrSchema.description}
          level={level}
        />
      );
    }
    if (nesting_mode === 'map') {
      return (
        <NestedMapField
          name={name}
          value={value || {}}
          onChange={onChange}
          attributes={attributes}
          description={attrSchema.description}
          level={level}
        />
      );
    }
  }

  // Determine type from schema or module variable
  const type = attrSchema?.type || moduleVar?.type;
  let baseType: string;
  let elementType: string | undefined;

  if (moduleVar?.type) {
    baseType = moduleVar.type.base;
    elementType = moduleVar.type.element?.base;
  } else {
    baseType = getBaseType(type);
    elementType = getElementType(type);
  }

  // Helper: convert ParsedVariableType attributes to TerraformAttributeSchema format
  const convertParsedAttrsToSchema = (
    parsedAttrs: Record<string, { type: ParsedVariableType; optional?: boolean; default?: any }>
  ): Record<string, TerraformAttributeSchema> => {
    const attrs: Record<string, TerraformAttributeSchema> = {};
    for (const [k, v] of Object.entries(parsedAttrs)) {
      // Use base type (not raw, which may contain HCL comments)
      // For container types like list(string), encode as ["list", "string"]
      let schemaType: any = v.type.base;
      if ((v.type.base === 'list' || v.type.base === 'set' || v.type.base === 'map') && v.type.element) {
        schemaType = [v.type.base, v.type.element.base];
      }

      attrs[k] = {
        type: schemaType,
        optional: v.optional,
        description: '',
      };
      // If this attribute itself is an object with attributes, carry nested_type through
      if (v.type.base === 'object' && v.type.attributes) {
        attrs[k].nested_type = {
          attributes: convertParsedAttrsToSchema(v.type.attributes),
          nesting_mode: 'single',
        };
      }
      // list/set of objects → nested list
      if ((v.type.base === 'list' || v.type.base === 'set') && v.type.element?.base === 'object' && v.type.element.attributes) {
        attrs[k].nested_type = {
          attributes: convertParsedAttrsToSchema(v.type.element.attributes),
          nesting_mode: v.type.base === 'set' ? 'set' : 'list',
        };
      }
      // map of objects → nested map
      if (v.type.base === 'map' && v.type.element?.base === 'object' && v.type.element.attributes) {
        attrs[k].nested_type = {
          attributes: convertParsedAttrsToSchema(v.type.element.attributes),
          nesting_mode: 'map',
        };
      }
    }
    return attrs;
  };

  // Module variable with object attributes (direct object type)
  if (moduleVar?.type?.base === 'object' && moduleVar.type.attributes) {
    return (
      <NestedSingleField
        name={name}
        value={value || {}}
        onChange={onChange}
        attributes={convertParsedAttrsToSchema(moduleVar.type.attributes)}
        description={moduleVar.description}
        level={level}
      />
    );
  }

  // Module variable: map(object({...})) — render structured fields per map entry
  if (moduleVar?.type?.base === 'map' && moduleVar.type.element?.base === 'object' && moduleVar.type.element.attributes) {
    return (
      <NestedMapField
        name={name}
        value={value || {}}
        onChange={onChange}
        attributes={convertParsedAttrsToSchema(moduleVar.type.element.attributes)}
        description={moduleVar.description}
        level={level}
      />
    );
  }

  // Module variable: list/set(object({...})) — render structured fields per list item
  if ((moduleVar?.type?.base === 'list' || moduleVar?.type?.base === 'set') && moduleVar.type.element?.base === 'object' && moduleVar.type.element.attributes) {
    return (
      <NestedListField
        name={name}
        value={value || []}
        onChange={onChange}
        attributes={convertParsedAttrsToSchema(moduleVar.type.element.attributes)}
        description={moduleVar.description}
        level={level}
      />
    );
  }

  switch (baseType) {
    case 'string':
      return <StringField name={name} value={value} onChange={onChange} attrSchema={attrSchema} moduleVar={moduleVar} />;
    case 'number':
      return <NumberField name={name} value={value} onChange={onChange} attrSchema={attrSchema} moduleVar={moduleVar} />;
    case 'bool':
      return <BoolField name={name} value={value} onChange={onChange} attrSchema={attrSchema} moduleVar={moduleVar} />;
    case 'list':
    case 'set':
      return <ListField name={name} value={value} onChange={onChange} attrSchema={attrSchema} moduleVar={moduleVar} elementType={elementType} />;
    case 'map':
      return <MapField name={name} value={value} onChange={onChange} attrSchema={attrSchema} moduleVar={moduleVar} />;
    default:
      return <StringField name={name} value={value} onChange={onChange} attrSchema={attrSchema} moduleVar={moduleVar} />;
  }
};

// ============================================================================
// Block Type Fields (block_types from provider schema)
// ============================================================================

const BlockTypeField: React.FC<{
  name: string;
  value: any;
  onChange: (value: any) => void;
  blockType: {
    nesting_mode: string;
    block: TerraformBlockSchema;
    min_items?: number;
    max_items?: number;
  };
  level: number;
}> = ({ name, value, onChange, blockType, level }) => {
  const attrs = blockType.block.attributes || {};

  if (blockType.nesting_mode === 'single') {
    return (
      <NestedSingleField
        name={name}
        value={value || {}}
        onChange={onChange}
        attributes={attrs}
        description={blockType.block.description}
        level={level}
      />
    );
  }

  if (blockType.nesting_mode === 'list' || blockType.nesting_mode === 'set') {
    return (
      <NestedListField
        name={name}
        value={value || []}
        onChange={onChange}
        attributes={attrs}
        description={blockType.block.description}
        level={level}
      />
    );
  }

  if (blockType.nesting_mode === 'map') {
    return (
      <NestedMapField
        name={name}
        value={value || {}}
        onChange={onChange}
        attributes={attrs}
        description={blockType.block.description}
        level={level}
      />
    );
  }

  return null;
};

// ============================================================================
// Main Form Renderer
// ============================================================================

export const TerraformFormRenderer: React.FC<FormRendererProps> = ({
  schema,
  moduleVariables,
  values,
  onChange,
  mode = 'create',
}) => {
  const updateField = (name: string, value: any) => {
    onChange({ ...values, [name]: value });
  };

  // Render from provider schema
  if (schema) {
    const attributes = schema.attributes || {};
    const blockTypes = schema.block_types || {};

    // Sort: required first, then optional, hide computed-only in create mode
    const requiredAttrs = Object.entries(attributes).filter(
      ([, a]) => a.required && !(mode === 'create' && a.computed && !a.optional),
    );
    const optionalAttrs = Object.entries(attributes).filter(
      ([, a]) => !a.required && a.optional && !(mode === 'create' && a.computed && !a.optional),
    );
    const computedAttrs = Object.entries(attributes).filter(
      ([, a]) => a.computed && !a.optional && !a.required,
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Required fields */}
        {requiredAttrs.length > 0 && (
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Required
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {requiredAttrs.map(([name, attr]) => (
                <SchemaField
                  key={name}
                  name={name}
                  value={values[name]}
                  onChange={v => updateField(name, v)}
                  attrSchema={attr}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Block types */}
        {Object.entries(blockTypes).map(([name, bt]) => (
          <BlockTypeField
            key={name}
            name={name}
            value={values[name]}
            onChange={v => updateField(name, v)}
            blockType={bt}
            level={0}
          />
        ))}

        {/* Optional fields (collapsed) */}
        {optionalAttrs.length > 0 && (
          <Accordion defaultExpanded={false} variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="overline">
                Optional ({optionalAttrs.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {optionalAttrs.map(([name, attr]) => (
                  <SchemaField
                    key={name}
                    name={name}
                    value={values[name]}
                    onChange={v => updateField(name, v)}
                    attrSchema={attr}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Computed fields (read-only info) */}
        {mode === 'edit' && computedAttrs.length > 0 && (
          <Accordion defaultExpanded={false} variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="overline">
                Computed ({computedAttrs.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {computedAttrs.map(([name, attr]) => (
                  <TextField
                    key={name}
                    size="small"
                    label={name}
                    value={values[name] ?? '(computed)'}
                    disabled
                    helperText={attr.description}
                    fullWidth
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    );
  }

  // Render from module variables
  if (moduleVariables) {
    const required = moduleVariables.filter(v => v.required);
    const optional = moduleVariables.filter(v => !v.required);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {required.length > 0 && (
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Required
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {required.map(v => (
                <SchemaField
                  key={v.name}
                  name={v.name}
                  value={values[v.name]}
                  onChange={val => updateField(v.name, val)}
                  moduleVar={v}
                />
              ))}
            </Box>
          </Box>
        )}
        {optional.length > 0 && (
          <Accordion defaultExpanded={false} variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="overline">
                Optional ({optional.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {optional.map(v => (
                  <SchemaField
                    key={v.name}
                    name={v.name}
                    value={values[v.name]}
                    onChange={val => updateField(v.name, val)}
                    moduleVar={v}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    );
  }

  return (
    <Typography color="text.secondary" variant="body2">
      No schema or module variables provided.
    </Typography>
  );
};
